import { AGENT_CONFIGURATION_URI_PATTERN } from "@app/lib/actions/mcp_internal_actions/input_schemas";
import {
  isRunAgentQueryResourceType,
  isRunAgentResultResourceType,
} from "@app/lib/actions/mcp_internal_actions/output_schemas";
import type { AgentMCPActionType } from "@app/types/actions";
import type { SubAgentExchange } from "@app/types/assistant/conversation";
import type { CallToolResult } from "@modelcontextprotocol/sdk/types.js";
import { z } from "zod";

const RunAgentParamsSchema = z.object({
  query: z.string(),
  childAgent: z.object({ uri: z.string() }).optional(),
});

/**
 * Reads a run_agent call as the exchange the chat shows: who was asked, what
 * they were asked, and what they answered. The task and the child come from the
 * call's inputs, so a running sub-agent already has both; the reply comes from
 * the output once it lands. Returns undefined for any other tool.
 */
export function getSubAgentExchange(
  action: Pick<
    AgentMCPActionType,
    "createdAt" | "internalMCPServerName" | "params"
  > & {
    output?: CallToolResult["content"] | null;
  }
): SubAgentExchange | undefined {
  if (action.internalMCPServerName !== "run_agent") {
    return undefined;
  }

  const output = action.output ?? [];
  const queryResource = output.find(isRunAgentQueryResourceType);
  const resultResource = output.find(isRunAgentResultResourceType);

  const params = RunAgentParamsSchema.safeParse(action.params);
  const task = params.success
    ? params.data.query
    : (queryResource?.resource.text ?? null);
  const childAgentId =
    (params.success && params.data.childAgent
      ? params.data.childAgent.uri.match(AGENT_CONFIGURATION_URI_PATTERN)?.[2]
      : undefined) ??
    queryResource?.resource.childAgentId ??
    null;

  if (!task || !childAgentId) {
    return undefined;
  }

  return {
    childAgentId,
    task,
    reply:
      resultResource && !resultResource.resource.delegatedMessageId
        ? resultResource.resource.text
        : null,
    createdAt: action.createdAt,
  };
}
